import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { getRoles, deleteRole } from "../../api";
import DataTableSubHeader from "../../Components/DataTableSubHeader/DataTableSubHeader";
import useTransformData from "../../hooks/useTransformData";
import { AddIcon } from "../../assets/icons";
import ItemsTable from "../../Components/ItemsTable/ItemsTable";
import AddRoleModal from "../../Components/RolesDetailsTable/AddRoleModal/AddRoleModal";
import EditRoleModal from "../../Components/RolesDetailsTable/EditRoleModal/EditRoleModal";
import DeleteModal from "../../Components/Common/DeleteModal/DeleteModal";
import "./Roles.scss";
import { useAppContext } from "../../Components/Context/AppContext.jsx";
import userHasPermission from "../../helpers/formTools/userHasPermission/index.js";
import AdminDropdown from "../../Components/AdminDropdown/AdminDropdown.jsx";
import Spinner from "../../Components/ProgressBar/Spinner.js";

const selectedHeaders = [
  { key: "name", label: "Nome ruolo", width: "300px", sortable: false },
  { key: "color", label: "Colore", width: "300px", sortable: false },
  { key: "authorities", label: "Autorità", width: "300px", sortable: false },
  { key: "actions", label: "", width: "300px", sortable: false },
];

const config = {
  fields: [
    { name: "id", type: "text" },
    { name: "name", type: "text" },
    { name: "color", type: "color" },
    { name: "authorities", type: "list" },
  ],
};

const RolesDetailsTable = () => {
  const [isMenuActive, setMenuActive] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState([]);
  const [selectedRole, setSelectedRole] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { userProfile } = useAppContext();
  const organization = JSON.parse(localStorage.getItem("organization"));
  const userCanRead = userHasPermission({userProfile, organization, resource: "ROLE", permission: "READ"});
  const userCanWrite = userHasPermission({userProfile, organization, resource: "ROLE", permission: "WRITE"});
  const userCanDelete = userHasPermission({userProfile, organization, resource: "ROLE", permission: "DELETE"});

  const handleViewRole = useCallback((item) => {
    setSelectedRole(item);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((item) => {
    setSelectedRole(item);
    setIsDeleteModalOpen(true);
  }, []);

  const transformedData = useTransformData(
    formData,
    handleViewRole,
    handleDeleteClick,
    config,
    userCanDelete
  );

  const breadcrumbsData = useMemo(
    () => [
      {
        id: 1,
        label: `Amministrazione ${localStorage.getItem("username")}`,
        iconUrl: true,
      },
      { id: 2, label: "RUOLI", iconUrl: "" },
    ],
    []
  );

  const fetchRoles = useCallback(async () => {
    const filter = {
      page: currentPage,
      pageSize: recordsPerPage,
    };
    try {
      const response = await getRoles(filter);
      setFormData(response.results);
      setTotalRecords(response.totalElements);
      setIsLoading(false);
    } catch (error) {
      toast.error("Error fetching roles");
    }
  }, [currentPage, recordsPerPage]);

  const handleDeleteRole = useCallback(async (id) => {
    try {
      await deleteRole(id);
      toast.success("Role deleted successfully");
      fetchRoles();
    } catch (error) {
      toast.error("Error deleting role");
    } finally {
      setIsDeleteModalOpen(false);
    }
  }, [fetchRoles]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRecordsPerPageChange = useCallback((newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  }, []);

  const subHeaderRightNavOptions = useMemo(
    () => ({
      icons: [
        userCanWrite && {
          key: "add",
          icon: <AddIcon fillColor="var(--archeo-color)" />,
          onClick: () => setModalOpen(true),
          label: "Aggiungi",
        },
      ].filter(Boolean),
      components: [
        <AdminDropdown defaultSelected="RUOLI" />,
      ],
    }),
    [userCanWrite]
  );

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return (
    <div className="container">
      <main className="page-main-content">
        <DataTableSubHeader
          title="RUOLI"
          breadcrumbsData={breadcrumbsData}
          subHeaderRightNavOptions={subHeaderRightNavOptions}
          isMenuActive={isMenuActive}
          setMenuActive={() => setMenuActive(!isMenuActive)}
        />
        {isLoading ? (
          <Spinner />
        ) : (
        <ItemsTable
          data={transformedData}
          headers={selectedHeaders}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
          userCanRead={userCanRead}
          />
        )}
        {modalOpen && (
          <AddRoleModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            fetchRoles={fetchRoles}
          />
        )}
        {isEditModalOpen && (
          <EditRoleModal
            show={isEditModalOpen}
            roleData={selectedRole}
            fetchRoles={fetchRoles}
            handleClose={() => setIsEditModalOpen(false)}
          />
        )}
        {isDeleteModalOpen && (
          <DeleteModal
            show={isDeleteModalOpen}
            item={selectedRole}
            itemType="Role"
            handleClose={() => setIsDeleteModalOpen(false)}
            handleDelete={handleDeleteRole}
          />
        )}
      </main>
    </div>
  );
};

export default RolesDetailsTable;
